import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import LearningCycle from './components/LearningCycle';
import FourPillars from './components/FourPillars';
import CourseGoals from './components/CourseGoals';
import TextbookSystem from './components/TextbookSystem';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import Seo from '@/components/seo/Seo';

function BackToTop() {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full border border-accent-400/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 z-50 cursor-pointer"
      aria-label={t('common.backToTop')}
    >
      <i className="ri-arrow-up-line text-base"></i>
    </button>
  );
}

export default function CurriculumPage() {
  const [mounted, setMounted] = useState(false);
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  useEffect(() => {
    setMounted(true);
  }, []);

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: lang === 'en' ? 'Home' : 'ホーム', item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: lang === 'en' ? 'Curriculum' : 'カリキュラム', item: 'https://butler-academy.jp/curriculum' },
    ],
  };

  const orgData = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: lang === 'en' ? 'The Japan Butler Academy' : '日本執事アカデミー',
    parentOrganization: {
      '@type': 'Organization',
      name: lang === 'en' ? 'The All Japan Butler Association' : '一般社団法人全日本執事協会',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: lang === 'en' ? '2-7-4 Aomi, Koto-ku, Tokyo' : '東京都江東区青海2-7-4',
    },
    telephone: '03-6824-4086',
  };

  if (!mounted) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-accent-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <Seo route="/curriculum" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgData) }}
      />

      <Header />
      <main>
        <HeroSection />

        <LearningCycle />
        <FourPillars />
        <CourseGoals />
        <TextbookSystem />
        <CTASection />
        <FAQSection />
        <RelatedPages />

        <footer className="py-12 bg-background-100/50 text-center">
          {String(t('curriculum.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-400 tracking-[0.04em]">{__s}</p>))}
        </footer>

        <BackToTop />
      </main>
      <Footer />
    </>
  );
}
